import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';

import {
  AccommodationType,
  AccommodationTypeValues,
} from '@/domain/impact-co2/entities/question.entity';
import {
  VenueDefaultDataAccommodation,
  VenueDefaultDatas,
} from '@/domain/project-venue/entities/project-venue.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { thStyle } from '@/ui/components/table/Table.utils';
import { translate } from '@/ui/i18n';
import { DefaultDatasContext } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasContext';
import {
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasQuizComponents';
import { ColorsRaw } from '@/ui/theme/colors-raw';

export function AccommodationForm({
  onEdit,
  venueDefaultDatas,
  forceEdit,
  onClose,
}: {
  onEdit(venueDatas: VenueDefaultDatas): void;
  venueDefaultDatas: VenueDefaultDatas;
  forceEdit?: boolean;
  onClose?: () => void;
}) {
  const { panelOpen } = useContext(DefaultDatasContext);
  const [isEditing, setIsEditing] = useState(forceEdit ? true : false);
  const [values, setValues] = useState(
    structuredClone(venueDefaultDatas.accommodation),
  );

  const disableSubmitActions = false; //TODO

  const addAccommodationToAnswer = (accommodation: AccommodationType) => {
    setValues([...values, { type: accommodation, percentage: 0 }]);
  };

  const updateAccommodationPercentage = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].percentage = Number(Number(value).toFixed(2));
    if (!isNaN(newValues[index].percentage)) {
      setValues(newValues);
    }
  };

  const removeAccommodation = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const handleEdit = () => {
    onEdit({ ...venueDefaultDatas, accommodation: values });
    if (!forceEdit) setIsEditing(false);
  };

  const handleRemoveClick = () => {
    if (onClose) onClose();
    else setIsEditing(false);
  };

  return (
    <div id="accommodation-form">
      {(isEditing && !!panelOpen) || !!forceEdit ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.what-accommodation-distribution`,
          )}
          form={
            <Form
              onSubmit={handleEdit}
              values={values}
              disableSubmit={disableSubmitActions}
              addAccommodationToAnswer={addAccommodationToAnswer}
              updateAccommodationPercentage={updateAccommodationPercentage}
              removeAccommodation={removeAccommodation}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={values.map((value) => {
            return `${translate(`ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.AccommodationTypeValues.${value.type}`)}: ${value.percentage}%`;
          })}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.what-accommodation-distribution`,
          )}
          isAnswered={values.length > 0}
        />
      )}
    </div>
  );
}

function Form({
  addAccommodationToAnswer,
  updateAccommodationPercentage,
  removeAccommodation,
  disableSubmit,
  onSubmit,
  values,
}: {
  onSubmit: () => void;
  addAccommodationToAnswer: (accommodation: AccommodationType) => void;
  disableSubmit: boolean;
  updateAccommodationPercentage: (value: string, index: number) => void;
  removeAccommodation: (index: number) => void;
  values: VenueDefaultDataAccommodation;
}) {
  const [selectedAccommodation, setSelectedAccommodation] = useState<
    AccommodationType | ''
  >('');
  const [percentageError, setPercentageError] = useState(false);

  const getSelectValues = () => {
    const selectedAccommodations = values.map((value) => value.type);

    return selectedAccommodations
      ? AccommodationTypeValues.filter(
          (acc) => !selectedAccommodations.includes(acc),
        )
      : AccommodationTypeValues;
  };

  const addAccommodation = () => {
    if (selectedAccommodation) {
      addAccommodationToAnswer(selectedAccommodation);
      setSelectedAccommodation('');
    }
  };

  const handleChangePercentage = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    setPercentageError(false);
    updateAccommodationPercentage(e.target.value, index);
  };

  const handleSubmit = () => {
    const percentages = values.map((val) => val.percentage);

    const totalPercentage =
      percentages.length > 0
        ? percentages.reduce((acc, value) => {
            return acc + value;
          })
        : 0;

    if (totalPercentage === 100) {
      onSubmit();
    } else {
      setPercentageError(true);
    }
  };

  return (
    <Stack>
      <Table align="right">
        <TableHead>
          <TableRow>
            <TableCell style={thStyle}>
              {translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.accommodation-type`,
              )}
            </TableCell>
            <TableCell style={thStyle}>
              {`${translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.distribution`,
              )} (%)`}
            </TableCell>
            <TableCell style={thStyle} />
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.map((value, index) => (
            <TableRow key={'selectedAccommodation' + index}>
              <TableCell>
                {translate(
                  `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.AccommodationTypeValues.${value.type}`,
                )}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <Box display="flex" alignItems="center">
                  <FSTextField
                    sx={{ width: '80px' }}
                    type="number"
                    textAlign="right"
                    value={value.percentage}
                    onChange={(e) => handleChangePercentage(e, index)}
                    error={percentageError}
                  />
                  <Typography sx={{ ml: 1 }} fontSize={16}>
                    %
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <Box display="flex" justifyContent="end">
                  <Button onClick={() => removeAccommodation(index)}>
                    <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4}>
              <Box display={'flex'} alignItems={'center'}>
                <FSTextField
                  select
                  label={translate(
                    'ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.accommodation-type',
                  )}
                  value={selectedAccommodation}
                  onChange={(e) =>
                    setSelectedAccommodation(
                      e.target.value as AccommodationType,
                    )
                  }
                  sx={{ width: '100%' }}
                >
                  {getSelectValues().map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(
                        `ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.AccommodationTypeValues.${value}`,
                      )}
                    </MenuItem>
                  ))}
                </FSTextField>

                <FSPrimaryButton sx={{ ml: 1 }} onClick={addAccommodation}>
                  {translate('Form.add')}
                </FSPrimaryButton>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {percentageError && (
        <Typography sx={{ color: ColorsRaw.error.Error, mt: 2 }}>
          {translate(
            'ProjectsVenue.DefaultDatas.Public.Forms.Accommodation.percentage-error',
          )}
        </Typography>
      )}

      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
